import { lazy, Suspense, useEffect, useState, ReactElement } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { useBootReset } from '@/shared/erpSession';
import { useMetadata } from '@/shared/hooks/useMetadata';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { ForcedPasswordChange } from '@/features/auth/ForcedPasswordChange';
import { PermissionGate } from '@/components/ui/PermissionGate';
import { HomePage } from '@/features/home/HomePage';

// Two helpers for wiring RBAC into the route tree:
//   - `gateGroup(node)` returns an <Outlet/> wrapped in a PermissionGate
//      to use as the `element` of a parent Route — every child Route
//      inside it is now gated.
//   - `g(node, el)` wraps a single leaf element. Use for routes that
//      stand alone (no nested children).
const gateGroup = (node: string): ReactElement => (
  <PermissionGate node={node}><Outlet /></PermissionGate>
);
const g = (node: string, el: ReactElement): ReactElement => (
  <PermissionGate node={node}>{el}</PermissionGate>
);

// Lazy-loaded feature pages — each lands in its own chunk so the initial
// bundle only ships login + home + layout. Every `{ Foo }` named export
// is re-shaped to a `default` export so React.lazy can consume it.
//
// NOTE: there used to be an idle warmup that import()'d every chunk after
// first mount. That parsed and EVALUATED all ~200 page modules into the JS
// heap (~most of a 183MB tab). The PWA service worker already precaches
// every chunk on disk, so navigation stays network-instant without it —
// a chunk's first visit just pays its own parse, hidden by SoftFallback.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const named = <K extends string>(loader: () => Promise<Record<K, React.ComponentType<any>>>, key: K) =>
  lazy(() => loader().then((m) => ({ default: m[key] })));

// Short-delay fallback: only show the spinner if the lazy chunk takes
// longer than ~180 ms. For chunks that resolve from the service-worker
// precache (virtually always), the user sees no flicker at all.
function SoftFallback() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 180);
    return () => clearTimeout(t);
  }, []);
  if (!visible) return null;
  return <div className="p-10 flex justify-center"><LoadingSpinner /></div>;
}

// Chart-heavy pages were the last two static imports — lazy like the rest so
// recharts + the BI tabs stay out of the entry bundle.
const DashboardPage = named(() => import('@/features/dashboard/DashboardPage'), 'DashboardPage');
const BIDashboardPage = named(() => import('@/features/bi/BIDashboardPage'), 'BIDashboardPage');
const CustomersPage = named(() => import('@/features/customers/CustomersPage'), 'CustomersPage');
const EmployeeListPage = named(() => import('@/features/hr/EmployeeListPage'), 'EmployeeListPage');
const EmployeeDetailPage = named(() => import('@/features/hr/EmployeeDetailPage'), 'EmployeeDetailPage');
const PositionsPage = named(() => import('@/features/hr/PositionsPage'), 'PositionsPage');
const PayRunListPage = named(() => import('@/features/payroll/PayRunListPage'), 'PayRunListPage');
const PayRunDetailPage = named(() => import('@/features/payroll/PayRunDetailPage'), 'PayRunDetailPage');
const PayrollConfigPage = named(() => import('@/features/payroll/PayrollConfigPage'), 'PayrollConfigPage');
const YearEndFormsPage = named(() => import('@/features/payroll/YearEndFormsPage'), 'YearEndFormsPage');
const BenefitPlansPage = named(() => import('@/features/benefits/BenefitPlansPage'), 'BenefitPlansPage');
const EnrollmentsPage = named(() => import('@/features/benefits/EnrollmentsPage'), 'EnrollmentsPage');
const EnrollmentDetail = named(() => import('@/features/benefits/EnrollmentDetail'), 'EnrollmentDetail');
const CompliancePage = named(() => import('@/features/benefits/CompliancePage'), 'CompliancePage');
const MyProfilePage = named(() => import('@/features/portal/MyProfilePage'), 'MyProfilePage');
const MyPayPage = named(() => import('@/features/portal/MyPayPage'), 'MyPayPage');
const MyBenefitsPage = named(() => import('@/features/portal/MyBenefitsPage'), 'MyBenefitsPage');
const MyTeamPage = named(() => import('@/features/portal/MyTeamPage'), 'MyTeamPage');
const CustomerFormPage = named(() => import('@/features/customers/CustomerFormPage'), 'CustomerFormPage');
const ArInvoicesPage = named(() => import('@/features/ar-invoices/ArInvoicesPage'), 'ArInvoicesPage');
const ArInvoiceDetailPage = named(() => import('@/features/ar-invoices/ArInvoiceDetailPage'), 'ArInvoiceDetailPage');
const ArInvoiceNewPage = named(() => import('@/features/ar-invoices/ArInvoiceNewPage'), 'ArInvoiceNewPage');
const ArInvoiceLinesPage = named(() => import('@/features/ar-invoices/ArInvoiceLinesPage'), 'ArInvoiceLinesPage');
const CreditNotesPage = named(() => import('@/features/credit-notes/CreditNotesPage'), 'CreditNotesPage');
const CreditNoteDetailPage = named(() => import('@/features/credit-notes/CreditNoteDetailPage'), 'CreditNoteDetailPage');
const CreditNoteNewPage = named(() => import('@/features/credit-notes/CreditNoteNewPage'), 'CreditNoteNewPage');
const ReceiptsPage = named(() => import('@/features/receipts/ReceiptsPage'), 'ReceiptsPage');
const ReceiptFormPage = named(() => import('@/features/receipts/ReceiptFormPage'), 'ReceiptFormPage');
const ReceiptNewPage = named(() => import('@/features/receipts/ReceiptNewPage'), 'ReceiptNewPage');
const ReceiptDetailPage = named(() => import('@/features/receipts/ReceiptDetailPage'), 'ReceiptDetailPage');
const ContraSettlementsPage = named(() => import('@/features/contra-settlements/ContraSettlementsPage'), 'ContraSettlementsPage');
const ContraSettlementDetailPage = named(() => import('@/features/contra-settlements/ContraSettlementDetailPage'), 'ContraSettlementDetailPage');
const ContractsPage = named(() => import('@/features/contracts/ContractsPage'), 'ContractsPage');
const ContractFormPage = named(() => import('@/features/contracts/ContractFormPage'), 'ContractFormPage');
const ContractDetailPage = named(() => import('@/features/contracts/ContractDetailPage'), 'ContractDetailPage');
const VendorsPage = named(() => import('@/features/vendors/VendorsPage'), 'VendorsPage');
const VendorFormPage = named(() => import('@/features/vendors/VendorFormPage'), 'VendorFormPage');
const ApInvoicesPage = named(() => import('@/features/ap-invoices/ApInvoicesPage'), 'ApInvoicesPage');
const ApInvoiceDetailPage = named(() => import('@/features/ap-invoices/ApInvoiceDetailPage'), 'ApInvoiceDetailPage');
const ApInvoiceFormPage = named(() => import('@/features/ap-invoices/ApInvoiceFormPage'), 'ApInvoiceFormPage');
const ScanInvoicePage = named(() => import('@/features/ap-invoices/ScanInvoicePage'), 'ScanInvoicePage');
const ScanInboxPage = named(() => import('@/features/ap-invoices/ScanInboxPage'), 'ScanInboxPage');
const ApInvoiceLinesPage = named(() => import('@/features/ap-invoices/ApInvoiceLinesPage'), 'ApInvoiceLinesPage');
const CashFlowPage = named(() => import('@/features/cashflow/CashFlowPage'), 'CashFlowPage');
const CollectionsPage = named(() => import('@/features/receivables/CollectionsPage'), 'CollectionsPage');
const VendorsHubPage = named(() => import('@/features/payables/VendorsHubPage'), 'VendorsHubPage');
const ExpenseCategoriesPage = named(() => import('@/features/payables/ExpenseCategoriesPage'), 'ExpenseCategoriesPage');
const PayBillsPage = named(() => import('@/features/payables/PayBillsPage'), 'PayBillsPage');
const BankFilesPage = named(() => import('@/features/payables/BankFilesPage'), 'BankFilesPage');
const BankFileDetailPage = named(() => import('@/features/payables/BankFileDetailPage'), 'BankFileDetailPage');
const CopilotPage = named(() => import('@/features/copilot/CopilotPage'), 'CopilotPage');
const PaymentsPage = named(() => import('@/features/payments/PaymentsPage'), 'PaymentsPage');
const PaymentFormPage = named(() => import('@/features/payments/PaymentFormPage'), 'PaymentFormPage');
const PaymentDetailPage = named(() => import('@/features/payments/PaymentDetailPage'), 'PaymentDetailPage');
const AccountsPage = named(() => import('@/features/accounts/AccountsPage'), 'AccountsPage');
const AccountLedgerPage = named(() => import('@/features/accounts/AccountLedgerPage'), 'AccountLedgerPage');
const BankAccountsPage = named(() => import('@/features/bank/BankAccountsPage'), 'BankAccountsPage');
const BankAccountFormPage = named(() => import('@/features/bank/BankAccountFormPage'), 'BankAccountFormPage');
const BankingPage = named(() => import('@/features/bank/BankingPage'), 'BankingPage');
const BankStatementImportPage = named(() => import('@/features/bank/BankStatementImportPage'), 'BankStatementImportPage');
const BankStatementDetailPage = named(() => import('@/features/bank/BankStatementDetailPage'), 'BankStatementDetailPage');
const BankReconciliationPage = named(() => import('@/features/bank/BankReconciliationPage'), 'BankReconciliationPage');
const JournalPage = named(() => import('@/features/journal/JournalPage'), 'JournalPage');
const JournalFormPage = named(() => import('@/features/journal/JournalFormPage'), 'JournalFormPage');
const JournalDetailPage = named(() => import('@/features/journal/JournalDetailPage'), 'JournalDetailPage');
const RecurringJournalsPage = named(() => import('@/features/journal/RecurringJournalsPage'), 'RecurringJournalsPage');
const RecurringJournalFormPage = named(() => import('@/features/journal/RecurringJournalFormPage'), 'RecurringJournalFormPage');
const CashFlowStatementReport = named(() => import('@/features/reports/CashFlowStatementReport'), 'CashFlowStatementReport');
const LedgerAuditReport = named(() => import('@/features/reports/LedgerAuditReport'), 'LedgerAuditReport');
const FxExposureReport = named(() => import('@/features/reports/FxExposureReport'), 'FxExposureReport');
const ArAgingReport = named(() => import('@/features/reports/ArAgingReport'), 'ArAgingReport');
const ApAgingReport = named(() => import('@/features/reports/ApAgingReport'), 'ApAgingReport');
const TrialBalanceReport = named(() => import('@/features/reports/TrialBalanceReport'), 'TrialBalanceReport');
const TaxSummaryReport = named(() => import('@/features/reports/TaxSummaryReport'), 'TaxSummaryReport');
const ControlReconciliationReport = named(() => import('@/features/reports/ControlReconciliationReport'), 'ControlReconciliationReport');
const InventoryValuationReport = named(() => import('@/features/reports/InventoryValuationReport'), 'InventoryValuationReport');
const ProfitLossReport = named(() => import('@/features/reports/ProfitLossReport'), 'ProfitLossReport');
const BalanceSheetReport = named(() => import('@/features/reports/BalanceSheetReport'), 'BalanceSheetReport');
const StatementsPage = named(() => import('@/features/reports/StatementsPage'), 'StatementsPage');
const CompanySettingsPage = named(() => import('@/features/admin/CompanySettingsPage'), 'CompanySettingsPage');
const FiscalPeriodsPage = named(() => import('@/features/admin/FiscalPeriodsPage'), 'FiscalPeriodsPage');
const PostingRulesPage = named(() => import('@/features/admin/PostingRulesPage'), 'PostingRulesPage');
const CustomFieldsPage = named(() => import('@/features/admin/CustomFieldsPage'), 'CustomFieldsPage');
const MenuManagerPage = named(() => import('@/features/admin/MenuManagerPage'), 'MenuManagerPage');
const ReportBuilderPage = named(() => import('@/features/admin/ReportBuilderPage'), 'ReportBuilderPage');
const FormDesignerPage = named(() => import('@/features/admin/FormDesignerPage'), 'FormDesignerPage');
const CustomObjectsPage = named(() => import('@/features/admin/CustomObjectsPage'), 'CustomObjectsPage');
const CustomObjectListPage = named(() => import('@/features/custom-objects/CustomObjectListPage'), 'CustomObjectListPage');
const CustomObjectFormPage = named(() => import('@/features/custom-objects/CustomObjectFormPage'), 'CustomObjectFormPage');
const CustomDashboardsPage = named(() => import('@/features/dashboards/DashboardsPage'), 'DashboardsPage');
const AutomationPage = named(() => import('@/features/admin/AutomationPage'), 'AutomationPage');
const ChecksPage = named(() => import('@/features/admin/ChecksPage'), 'ChecksPage');
const WorkflowsPage = named(() => import('@/features/admin/WorkflowsPage'), 'WorkflowsPage');
const ProceduresPage = named(() => import('@/features/procedures/ProceduresPage'), 'ProceduresPage');
const ProcedureEditorPage = named(() => import('@/features/procedures/ProcedureEditorPage'), 'ProcedureEditorPage');
const GovernancePage = named(() => import('@/features/admin/GovernancePage'), 'GovernancePage');
const ApiTokensPage = named(() => import('@/features/admin/ApiTokensPage'), 'ApiTokensPage');
const CustomizationHub = named(() => import('@/features/admin/CustomizationHub'), 'CustomizationHub');
const NotificationsPage = named(() => import('@/features/notifications/NotificationsPage'), 'NotificationsPage');
const WebhooksPage = named(() => import('@/features/admin/WebhooksPage'), 'WebhooksPage');
const IntegrationsPage = named(() => import('@/features/admin/IntegrationsPage'), 'IntegrationsPage');
// The QuickBooks Online transfer log (docs/superpowers/specs/2026-10-22-quickbooks-online-connection.md);
// the connection itself is on the Integrations page.
const QuickBooksTransfersPage = named(() => import('@/features/receivables/QuickBooksTransfersPage'), 'QuickBooksTransfersPage');
const NewCompanyPage = named(() => import('@/features/admin/NewCompanyPage'), 'NewCompanyPage');
const DocumentNumberingPage = named(() => import('@/features/admin/DocumentNumberingPage'), 'DocumentNumberingPage');
const DocumentTypesPage = named(() => import('@/features/admin/DocumentTypesPage'), 'DocumentTypesPage');
const DocumentTemplatesPage = named(() => import('@/features/admin/DocumentTemplatesPage'), 'DocumentTemplatesPage');
const RoleWorkspacePage = named(() => import('@/features/admin/RoleWorkspacePage'), 'RoleWorkspacePage');
const UsersPage = named(() => import('@/features/admin/UsersPage'), 'UsersPage');
const AuditEventsPage = named(() => import('@/features/admin/AuditEventsPage'), 'AuditEventsPage');
const BranchesPage = named(() => import('@/features/admin/BranchesPage'), 'BranchesPage');
const BranchFormPage = named(() => import('@/features/admin/BranchFormPage'), 'BranchFormPage');
const DepartmentsPage = named(() => import('@/features/admin/DepartmentsPage'), 'DepartmentsPage');
const LedgerSettingsPage = named(() => import('@/features/ledger/LedgerSettingsPage'), 'LedgerSettingsPage');
const ReceivablesSettingsPage = named(() => import('@/features/receivables/ReceivablesSettingsPage'), 'ReceivablesSettingsPage');
const ReportsPage = named(() => import('@/features/receivables/ReportsPage'), 'ReportsPage');
const ArReportBuilderPage = named(() => import('@/features/receivables/ReportsPage'), 'ReportBuilderPage');
const AppearancePage = named(() => import('@/features/settings/AppearancePage'), 'AppearancePage');
const SecurityPage = named(() => import('@/features/settings/SecurityPage'), 'SecurityPage');
const ChangePasswordPage = named(() => import('@/features/settings/ChangePasswordPage'), 'ChangePasswordPage');
const EmailConfigPage = named(() => import('@/features/settings/EmailConfigPage'), 'EmailConfigPage');
const EmailTemplatesPage = named(() => import('@/features/settings/EmailTemplatesPage'), 'EmailTemplatesPage');
const ComingSoonPage = named(() => import('@/features/admin/ComingSoonPage'), 'ComingSoonPage');
const ItemsPage = named(() => import('@/features/items/ItemsPage'), 'ItemsPage');
const ItemFormPage = named(() => import('@/features/items/ItemFormPage'), 'ItemFormPage');
const ItemFamiliesPage = named(() => import('@/features/item-families/ItemFamiliesPage'), 'ItemFamiliesPage');
const ItemFamilyFormPage = named(() => import('@/features/item-families/ItemFamilyFormPage'), 'ItemFamilyFormPage');
const B2bAdminLayout = named(() => import('@/features/b2b-admin/B2bShared'), 'B2bAdminLayout');
const B2bDashboardPage = named(() => import('@/features/b2b-admin/B2bDashboardPage'), 'B2bDashboardPage');
const B2bOrdersPage = named(() => import('@/features/b2b-admin/B2bPages'), 'B2bOrdersPage');
const B2bOrderPage = named(() => import('@/features/b2b-admin/B2bOrderPage'), 'B2bOrderPage');
const B2bCustomersPage = named(() => import('@/features/b2b-admin/B2bPages'), 'B2bCustomersPage');
const B2bUsersPage = named(() => import('@/features/b2b-admin/B2bPages'), 'B2bUsersPage');
const B2bProductsPage = named(() => import('@/features/b2b-admin/B2bPages'), 'B2bProductsPage');
const B2bInventoryPage = named(() => import('@/features/b2b-admin/B2bPages'), 'B2bInventoryPage');
const B2bSettingsPage = named(() => import('@/features/b2b-admin/B2bSettingsPage'), 'B2bSettingsPage');
const B2bAuditPage = named(() => import('@/features/b2b-admin/B2bPages'), 'B2bAuditPage');
const SalesCatalogsPage = named(() => import('@/features/sales-catalogs/SalesCatalogsPage'), 'SalesCatalogsPage');
const SalesCatalogFormPage = named(() => import('@/features/sales-catalogs/SalesCatalogFormPage'), 'SalesCatalogFormPage');
const UomsPage = named(() => import('@/features/uoms/UomsPage'), 'UomsPage');
const UomConversionsPage = named(() => import('@/features/uoms/UomConversionsPage'), 'UomConversionsPage');
const BomsPage = named(() => import('@/features/boms/BomsPage'), 'BomsPage');
const BomFormPage = named(() => import('@/features/boms/BomFormPage'), 'BomFormPage');
const BomDetailPage = named(() => import('@/features/boms/BomDetailPage'), 'BomDetailPage');
const WarehousesPage = named(() => import('@/features/warehouses/WarehousesPage'), 'WarehousesPage');
const WarehouseFormPage = named(() => import('@/features/warehouses/WarehouseFormPage'), 'WarehouseFormPage');
const OnHandPage = named(() => import('@/features/inventory/OnHandPage'), 'OnHandPage');
const TransactionsPage = named(() => import('@/features/inventory/TransactionsPage'), 'TransactionsPage');
const MovementLogPage = named(() => import('@/features/inventory/MovementLogPage'), 'MovementLogPage');
const NewTransactionPage = named(() => import('@/features/inventory/NewTransactionPage'), 'NewTransactionPage');
const TransactionDetailPage = named(() => import('@/features/inventory/TransactionDetailPage'), 'TransactionDetailPage');
const ProjectsPage = named(() => import('@/features/projects/ProjectsPage'), 'ProjectsPage');
const ProjectFormPage = named(() => import('@/features/projects/ProjectFormPage'), 'ProjectFormPage');
const ProjectDetailPage = named(() => import('@/features/projects/ProjectDetailPage'), 'ProjectDetailPage');
const TimeEntriesPage = named(() => import('@/features/projects/TimeEntriesPage'), 'TimeEntriesPage');
const CostCodesPage = named(() => import('@/features/cost-codes/CostCodesPage'), 'CostCodesPage');
const ChangeOrderDetailPage = named(() => import('@/features/change-orders/ChangeOrderDetailPage'), 'ChangeOrderDetailPage');
const RfiListPage = named(() => import('@/features/rfis/RfiListPage'), 'RfiListPage');
const RfiDetailPage = named(() => import('@/features/rfis/RfiDetailPage'), 'RfiDetailPage');
const SubmittalListPage = named(() => import('@/features/submittals/SubmittalListPage'), 'SubmittalListPage');
const SubmittalDetailPage = named(() => import('@/features/submittals/SubmittalDetailPage'), 'SubmittalDetailPage');
const PayApplicationDetailPage = named(() => import('@/features/pay-applications/PayApplicationDetailPage'), 'PayApplicationDetailPage');
const LotsPage = named(() => import('@/features/inventory/LotsPage'), 'LotsPage');
const SerialsPage = named(() => import('@/features/inventory/SerialsPage'), 'SerialsPage');
const SalesOrdersPage = named(() => import('@/features/sales-orders/SalesOrdersPage'), 'SalesOrdersPage');
const SalesOrderNewPage = named(() => import('@/features/sales-orders/SalesOrderNewPage'), 'SalesOrderNewPage');
const SalesOrderDetailPage = named(() => import('@/features/sales-orders/SalesOrderDetailPage'), 'SalesOrderDetailPage');
const SalesOrderLinesPage = named(() => import('@/features/sales-orders/SalesOrderLinesPage'), 'SalesOrderLinesPage');
const QuotesPage = named(() => import('@/features/quotes/QuotesPage'), 'QuotesPage');
const QuoteNewPage = named(() => import('@/features/quotes/QuoteNewPage'), 'QuoteNewPage');
const AgentsPage = named(() => import('@/features/agents/AgentsPage'), 'AgentsPage');
const TradeShowsPage = named(() => import('@/features/trade-shows/TradeShowsPage'), 'TradeShowsPage');
const LeadsPage = named(() => import('@/features/leads/LeadsPage'), 'LeadsPage');
const LeadDetailPage = named(() => import('@/features/leads/LeadDetailPage'), 'LeadDetailPage');
const OpportunitiesPage = named(() => import('@/features/opportunities/OpportunitiesPage'), 'OpportunitiesPage');
const OpportunityFormPage = named(() => import('@/features/opportunities/OpportunityFormPage'), 'OpportunityFormPage');
const ShipmentsPage = named(() => import('@/features/shipments/ShipmentsPage'), 'ShipmentsPage');
const ShipmentFormPage = named(() => import('@/features/shipments/ShipmentFormPage'), 'ShipmentFormPage');
const ShipmentDetailPage = named(() => import('@/features/shipments/ShipmentDetailPage'), 'ShipmentDetailPage');
const PacksPage = named(() => import('@/features/packs/PacksPage'), 'PacksPage');
const PackFormPage = named(() => import('@/features/packs/PackFormPage'), 'PackFormPage');
const InventoryStatusesPage = named(() => import('@/features/inventory/InventoryStatusesPage'), 'InventoryStatusesPage');
const WarehouseTransfersPage = named(() => import('@/features/inventory/WarehouseTransfersPage'), 'WarehouseTransfersPage');
const WarehouseTransferDetailPage = named(() => import('@/features/inventory/WarehouseTransferDetailPage'), 'WarehouseTransferDetailPage');
const InventoryCountsListPage = named(() => import('@/features/inventory-counts/InventoryCountsListPage'), 'InventoryCountsListPage');
const NewInventoryCountPage = named(() => import('@/features/inventory-counts/NewInventoryCountPage'), 'NewInventoryCountPage');
const InventoryCountDetailPage = named(() => import('@/features/inventory-counts/InventoryCountDetailPage'), 'InventoryCountDetailPage');
const BinsListPage = named(() => import('@/features/wms/BinsListPage'), 'BinsListPage');
const ZonesListPage = named(() => import('@/features/wms/ZonesListPage'), 'ZonesListPage');
const SlottingListPage = named(() => import('@/features/wms/SlottingListPage'), 'SlottingListPage');
const CycleCountPage = named(() => import('@/features/wms/CycleCountPage'), 'CycleCountPage');
const WavesListPage = named(() => import('@/features/wms/WavesListPage'), 'WavesListPage');
const WavePlannerPage = named(() => import('@/features/wms/WavePlannerPage'), 'WavePlannerPage');
const OutboundPlannerPage = named(() => import('@/features/wms/OutboundPlannerPage'), 'OutboundPlannerPage');
const WaveDetailPage = named(() => import('@/features/wms/WaveDetailPage'), 'WaveDetailPage');
const TasksListPage = named(() => import('@/features/wms/TasksListPage'), 'TasksListPage');
const AsnsListPage = named(() => import('@/features/wms/AsnsListPage'), 'AsnsListPage');
const AsnNewPage = named(() => import('@/features/wms/AsnNewPage'), 'AsnNewPage');
const DockListPage = named(() => import('@/features/wms/DockListPage'), 'DockListPage');
const GoodsReceiptsListPage = named(() => import('@/features/wms/GoodsReceiptsListPage'), 'GoodsReceiptsListPage');
const GoodsReceiptDetailPage = named(() => import('@/features/wms/GoodsReceiptDetailPage'), 'GoodsReceiptDetailPage');
const GoodsReceiptNewPage = named(() => import('@/features/wms/GoodsReceiptNewPage'), 'GoodsReceiptNewPage');
const LicensePlatesListPage = named(() => import('@/features/wms/LicensePlatesListPage'), 'LicensePlatesListPage');
const ForecastsPage = named(() => import('@/features/forecasts/ForecastsPage'), 'ForecastsPage');
const PriceListsPage = named(() => import('@/features/sales/PriceListsPage'), 'PriceListsPage');
const PriceListFormPage = named(() => import('@/features/sales/PriceListFormPage'), 'PriceListFormPage');
const VendorPriceListsPage = named(() => import('@/features/payables/vendor-price-lists/VendorPriceListsPage'), 'VendorPriceListsPage');
const VendorPriceListFormPage = named(() => import('@/features/payables/vendor-price-lists/VendorPriceListFormPage'), 'VendorPriceListFormPage');
const MrpRunsPage = named(() => import('@/features/mrp/MrpRunsPage'), 'MrpRunsPage');
const MrpRunDetailPage = named(() => import('@/features/mrp/MrpRunDetailPage'), 'MrpRunDetailPage');
const PurchaseOrdersPage = named(() => import('@/features/purchase-orders/PurchaseOrdersPage'), 'PurchaseOrdersPage');
const PurchaseOrderNewPage = named(() => import('@/features/purchase-orders/PurchaseOrderNewPage'), 'PurchaseOrderNewPage');
const PurchaseOrderDetailPage = named(() => import('@/features/purchase-orders/PurchaseOrderDetailPage'), 'PurchaseOrderDetailPage');
const PurchaseOrderLinesPage = named(() => import('@/features/purchase-orders/PurchaseOrderLinesPage'), 'PurchaseOrderLinesPage');
const PurchaseRequisitionsPage = named(() => import('@/features/purchase-requisitions/PurchaseRequisitionsPage'), 'PurchaseRequisitionsPage');
const PurchaseRequisitionFormPage = named(() => import('@/features/purchase-requisitions/PurchaseRequisitionFormPage'), 'PurchaseRequisitionFormPage');
const PurchaseRequisitionDetailPage = named(() => import('@/features/purchase-requisitions/PurchaseRequisitionDetailPage'), 'PurchaseRequisitionDetailPage');
const WorkCentersPage = named(() => import('@/features/production/WorkCentersPage'), 'WorkCentersPage');
const WorkCenterGroupsPage = named(() => import('@/features/production/WorkCenterGroupsPage'), 'WorkCenterGroupsPage');
const ManufacturingOrdersPage = named(() => import('@/features/production/ManufacturingOrdersPage'), 'ManufacturingOrdersPage');
const ManufacturingOrderDetailPage = named(() => import('@/features/production/ManufacturingOrderDetailPage'), 'ManufacturingOrderDetailPage');
const ReportProductionFormPage = named(() => import('@/features/production/ReportProductionFormPage'), 'ReportProductionFormPage');
const ReportProductionListPage = named(() => import('@/features/production/ReportProductionListPage'), 'ReportProductionListPage');
const RoutingsPage = named(() => import('@/features/production/RoutingsPage'), 'RoutingsPage');
const RoutingFormPage = named(() => import('@/features/production/RoutingFormPage'), 'RoutingFormPage');
const CapacityPlanPage = named(() => import('@/features/production/CapacityPlanPage'), 'CapacityPlanPage');
const ProductionSchedulePage = named(() => import('@/features/production/ProductionSchedulePage'), 'ProductionSchedulePage');
const ProductionGanttPage = named(() => import('@/features/production/ProductionGanttPage'), 'ProductionGanttPage');
const ProductionStatsPage = named(() => import('@/features/production/ProductionStatsPage'), 'ProductionStatsPage');
const ProductionSettingsPage = named(() => import('@/features/production/ProductionSettingsPage'), 'ProductionSettingsPage');
const ServiceJobsPage = named(() => import('@/features/service/ServiceJobsPage'), 'ServiceJobsPage');
const ServiceJobDetailPage = named(() => import('@/features/service/ServiceJobDetailPage'), 'ServiceJobDetailPage');
const ServiceAssetsPage = named(() => import('@/features/service/ServiceAssetsPage'), 'ServiceAssetsPage');
const ServiceAssetFormPage = named(() => import('@/features/service/ServiceAssetFormPage'), 'ServiceAssetFormPage');
const ServiceAssetDetailPage = named(() => import('@/features/service/ServiceAssetDetailPage'), 'ServiceAssetDetailPage');
const ServiceRmasPage = named(() => import('@/features/service/ServiceRmasPage'), 'ServiceRmasPage');
const ServiceRmaDetailPage = named(() => import('@/features/service/ServiceRmaDetailPage'), 'ServiceRmaDetailPage');
const ServiceContractsPage = named(() => import('@/features/service/ServiceContractsPage'), 'ServiceContractsPage');
const ServiceContractDetailPage = named(() => import('@/features/service/ServiceContractDetailPage'), 'ServiceContractDetailPage');
const DispatchBoardPage = named(() => import('@/features/service/DispatchBoardPage'), 'DispatchBoardPage');
const ServiceCallsPage = named(() => import('@/features/service/ServiceCallsPage'), 'ServiceCallsPage');
const ServiceCallFormPage = named(() => import('@/features/service/ServiceCallFormPage'), 'ServiceCallFormPage');
const ServiceCallDetailPage = named(() => import('@/features/service/ServiceCallDetailPage'), 'ServiceCallDetailPage');

function PageFallback() {
  return <SoftFallback />;
}

function Protected({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, ready, user } = useAuth();
  // Hydrate the Entity Registry once authenticated; gate the app on it so the
  // registry is always present before any detail page (EntityDetailExtras etc.)
  // renders. On a /meta failure the hook still flips ready=true (empty registry)
  // so the app never hangs — registry-driven cards just degrade to nothing.
  const { ready: metaReady } = useMetadata();
  // history-1: the page this tab booted at belonged to another user or company.
  const dropRestoredBoot = useBootReset();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (dropRestoredBoot) return <Navigate to="/" replace />;
  // A temporary password issued by an admin must be replaced before anything
  // else loads; the API refuses every other call with password_change_required.
  // Show only the change-password card, without the app shell: the shell's
  // menu cannot load the user's permissions yet and fell back to every module.
  if (user?.must_change_password) return <ForcedPasswordChange />;
  if (!metaReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route element={<Protected><AppLayout /></Protected>}>
          {/* BI dashboard tabs are now URL-driven (/bi/<tab>) so they live in
              the Dashboard sub-nav. '/' redirects to the Summary tab. */}
          <Route index element={g('home', <HomePage />)} />
          <Route path="bi" element={<Navigate to="/bi/summary" replace />} />
          <Route path="bi/:tab" element={<BIDashboardPage />} />
          <Route path="operations" element={<DashboardPage />} />

          <Route path="items" element={gateGroup('products.items')}>
            <Route index element={<ItemsPage />} />
            <Route path="new" element={<ItemFormPage />} />
            <Route path=":id" element={<ItemFormPage />} />
            <Route path=":id/edit" element={<ItemFormPage />} />
          </Route>
          <Route path="item-families" element={gateGroup('products.families')}>
            <Route index element={<ItemFamiliesPage />} />
            <Route path="new" element={<ItemFamilyFormPage />} />
            <Route path=":id" element={<ItemFamilyFormPage />} />
          </Route>
          {/* Sales catalogs edit under the items node (no permission of their
              own; the server gates the same way). */}
          <Route path="sales-catalogs" element={gateGroup('products.items')}>
            <Route index element={<SalesCatalogsPage />} />
            <Route path="new" element={<SalesCatalogFormPage />} />
            <Route path=":id" element={<SalesCatalogFormPage />} />
          </Route>
          <Route path="uoms" element={g('products.uoms', <UomsPage />)} />
          <Route path="uom-conversions" element={g('products.uom_conversions', <UomConversionsPage />)} />
          <Route path="warehouses" element={gateGroup('inventory.warehouses')}>
            <Route index element={<WarehousesPage />} />
            <Route path="new" element={<WarehouseFormPage />} />
            <Route path=":id/edit" element={<WarehouseFormPage />} />
            <Route path=":id" element={<WarehouseFormPage />} />
          </Route>
          <Route path="projects" element={gateGroup('projects.projects')}>
            <Route index element={<ProjectsPage />} />
            <Route path="new" element={<ProjectFormPage />} />
            <Route path=":id" element={<ProjectDetailPage />} />
            <Route path=":id/edit" element={<ProjectFormPage />} />
          </Route>
          <Route path="time-entries" element={g('projects.time', <TimeEntriesPage />)} />
          <Route path="cost-codes" element={g('projects.cost_codes', <CostCodesPage />)} />
          <Route path="change-orders/:id" element={g('projects.change_orders', <ChangeOrderDetailPage />)} />
          <Route path="rfis" element={g('projects.rfis', <RfiListPage />)} />
          <Route path="rfis/:id" element={g('projects.rfis', <RfiDetailPage />)} />
          <Route path="submittals" element={g('projects.submittals', <SubmittalListPage />)} />
          <Route path="submittals/:id" element={g('projects.submittals', <SubmittalDetailPage />)} />
          <Route path="pay-applications/:id" element={g('projects.pay_applications', <PayApplicationDetailPage />)} />
          <Route path="sales-orders" element={gateGroup('sales.orders')}>
            <Route index element={<SalesOrdersPage />} />
            <Route path="new" element={<SalesOrderNewPage />} />
            {/* The order-lines log. The static segment outranks :id below. */}
            <Route path="lines" element={<SalesOrderLinesPage />} />
            <Route path=":id" element={<SalesOrderDetailPage />} />
            <Route path=":id/edit" element={<SalesOrderDetailPage />} />
          </Route>
          <Route path="quotes" element={gateGroup('sales.orders')}>
            <Route index element={<QuotesPage />} />
            <Route path="new" element={<QuoteNewPage />} />
            {/* Quotes are sales_orders (is_quote=true) on the same /sales-orders
                API, so the detail view reuses the Sales Order page — it adapts to
                quotes (title, workflow, valid-until, convert/revise, /quotes crumbs). */}
            <Route path=":id" element={<SalesOrderDetailPage />} />
            {/* Legacy edit route retired: the detail page above edits inline. */}
            <Route path=":id/edit" element={<Navigate to=".." relative="path" replace />} />
          </Route>
          <Route path="shipments" element={gateGroup('sales.shipments')}>
            <Route index element={<ShipmentsPage />} />
            <Route path="new" element={<ShipmentFormPage />} />
            <Route path=":id" element={<ShipmentDetailPage />} />
            {/* Legacy edit route retired: the detail page above edits drafts inline
                (ShipmentFormPage stays for /shipments/new creation only). */}
            <Route path=":id/edit" element={<Navigate to=".." relative="path" replace />} />
          </Route>
          <Route path="packs" element={gateGroup('inventory.packs')}>
            <Route index element={<PacksPage />} />
            <Route path="new" element={<PackFormPage />} />
            <Route path=":id" element={<PackFormPage />} />
          </Route>
          <Route path="agents" element={g('sales.agents', <AgentsPage />)} />
          {/* Rep-app W5: trade shows, the leads the Sales app captured, opportunities. */}
          <Route path="sales/trade-shows" element={g('sales.leads', <TradeShowsPage />)} />
          <Route path="sales/trade-shows/:id" element={g('sales.leads', <TradeShowsPage />)} />
          <Route path="sales/leads" element={g('sales.leads', <LeadsPage />)} />
          <Route path="sales/leads/:id" element={g('sales.leads', <LeadDetailPage />)} />
          <Route path="sales/opportunities" element={g('sales.opportunities', <OpportunitiesPage />)} />
          <Route path="sales/opportunities/new" element={g('sales.opportunities', <OpportunityFormPage />)} />
          <Route path="sales/opportunities/:id" element={g('sales.opportunities', <OpportunityFormPage />)} />
          <Route path="forecasts" element={g('sales.forecasts', <ForecastsPage />)} />
          {/* B2B Admin Portal: 2FA on the account (B2bAdminLayout; the server refuses without) and
              a node per page. Customers' own portal is another entry, /b2b, with its own login. */}
          <Route path="sales/b2b" element={<PermissionGate node="sales.b2b"><B2bAdminLayout /></PermissionGate>}>
            <Route index element={g('sales.b2b.orders', <B2bDashboardPage />)} />
            <Route path="orders" element={g('sales.b2b.orders', <B2bOrdersPage />)} />
            <Route path="orders/:id" element={g('sales.b2b.orders', <B2bOrderPage />)} />
            <Route path="customers" element={g('sales.b2b.admin', <B2bCustomersPage />)} />
            <Route path="users" element={g('sales.b2b.admin', <B2bUsersPage />)} />
            <Route path="products" element={g('sales.b2b.admin', <B2bProductsPage />)} />
            <Route path="inventory" element={g('sales.b2b.orders', <B2bInventoryPage />)} />
            <Route path="settings" element={g('sales.b2b.admin', <B2bSettingsPage />)} />
            <Route path="audit" element={g('sales.b2b.admin', <B2bAuditPage />)} />
          </Route>
          <Route path="sales/price-lists" element={gateGroup('sales.price_lists')}>
            <Route index element={<PriceListsPage />} />
            <Route path="new" element={<PriceListFormPage />} />
            <Route path=":id" element={<PriceListFormPage />} />
          </Route>
          <Route path="purchase-orders" element={gateGroup('payables.purchase_orders')}>
            <Route index element={<PurchaseOrdersPage />} />
            {/* "+ New" = unnumbered stub straight into the detail form (like sales
                orders); the legacy /:id/edit form is retired — drafts edit inline. */}
            <Route path="new" element={<PurchaseOrderNewPage />} />
            {/* The PO-lines log. The static segment outranks :id below. */}
            <Route path="lines" element={<PurchaseOrderLinesPage />} />
            <Route path=":id" element={<PurchaseOrderDetailPage />} />
            <Route path=":id/edit" element={<Navigate to=".." relative="path" replace />} />
          </Route>
          <Route path="payables/vendor-price-lists" element={gateGroup('payables.vendor_price_lists')}>
            <Route index element={<VendorPriceListsPage />} />
            <Route path="new" element={<VendorPriceListFormPage />} />
            <Route path=":id" element={<VendorPriceListFormPage />} />
          </Route>
          <Route path="purchase-requisitions" element={gateGroup('payables.purchase_requisitions')}>
            <Route index element={<PurchaseRequisitionsPage />} />
            <Route path="new" element={<PurchaseRequisitionFormPage />} />
            <Route path=":id" element={<PurchaseRequisitionDetailPage />} />
            <Route path=":id/edit" element={<PurchaseRequisitionFormPage />} />
          </Route>
          <Route path="work-centers" element={g('production.settings.work_centers', <WorkCentersPage />)} />
          <Route path="work-center-groups" element={g('production.settings.work_center_groups', <WorkCenterGroupsPage />)} />
          <Route path="routings" element={gateGroup('production.settings.routings')}>
            <Route index element={<RoutingsPage />} />
            <Route path="new" element={<RoutingFormPage />} />
            <Route path=":id" element={<RoutingFormPage />} />
          </Route>
          <Route path="capacity-plan" element={g('production.capacity_plan', <CapacityPlanPage />)} />
          <Route path="production-schedule" element={g('production.schedule', <ProductionSchedulePage />)} />
          <Route path="production-gantt" element={g('production.gantt', <ProductionGanttPage />)} />
          <Route path="production-stats" element={g('production.statistics', <ProductionStatsPage />)} />
          <Route path="production/settings" element={g('production.settings', <ProductionSettingsPage />)} />
          <Route path="manufacturing-orders" element={gateGroup('production.manufacturing_orders')}>
            <Route index element={<ManufacturingOrdersPage />} />
            <Route path="report-production" element={g('production.report_production', <ReportProductionListPage />)} />
            <Route path="report-production/new" element={g('production.report_production', <ReportProductionFormPage />)} />
            <Route path=":id" element={<ManufacturingOrderDetailPage />} />
            <Route path=":moId/report-production" element={g('production.report_production', <ReportProductionFormPage />)} />
          </Route>
          <Route path="mrp/runs" element={gateGroup('production.mrp_runs')}>
            <Route index element={<MrpRunsPage />} />
            <Route path=":id" element={<MrpRunDetailPage />} />
          </Route>
          <Route path="inventory">
            <Route path="on-hand" element={g('inventory.on_hand', <OnHandPage />)} />
            <Route path="transactions" element={g('inventory.transactions', <TransactionsPage />)} />
            <Route path="movements" element={g('inventory.transactions', <MovementLogPage />)} />
            <Route path="transactions/:id" element={g('inventory.transactions', <TransactionDetailPage />)} />
            <Route path="new" element={g('inventory.transactions', <NewTransactionPage />)} />
            <Route path="lots" element={g('inventory.lots', <LotsPage />)} />
            <Route path="serials" element={g('inventory.serials', <SerialsPage />)} />
            <Route path="statuses" element={g('inventory.statuses', <InventoryStatusesPage />)} />
            <Route path="transfers" element={g('inventory.transfers', <WarehouseTransfersPage />)} />
            <Route path="transfers/new" element={g('inventory.transfers', <WarehouseTransferDetailPage />)} />
            <Route path="transfers/:id" element={g('inventory.transfers', <WarehouseTransferDetailPage />)} />
          </Route>
          <Route path="inventory-counts" element={gateGroup('inventory.counts')}>
            <Route index element={<InventoryCountsListPage />} />
            <Route path="new" element={<NewInventoryCountPage />} />
            <Route path=":id" element={<InventoryCountDetailPage />} />
          </Route>
          <Route path="boms" element={gateGroup('production.settings.boms')}>
            <Route index element={<BomsPage />} />
            <Route path="new" element={<BomFormPage />} />
            <Route path=":id" element={<BomDetailPage />} />
          </Route>

          <Route path="wms/bins" element={gateGroup('wms.bins')}>
            <Route index element={<BinsListPage />} />
          </Route>
          <Route path="wms/zones" element={gateGroup('wms.bins')}>
            <Route index element={<ZonesListPage />} />
          </Route>
          <Route path="wms/slotting" element={gateGroup('wms.bins')}>
            <Route index element={<SlottingListPage />} />
          </Route>
          <Route path="wms/cycle-count" element={gateGroup('wms.tasks')}>
            <Route index element={<CycleCountPage />} />
          </Route>
          <Route path="wms/waves" element={gateGroup('wms.waves')}>
            <Route index element={<WavesListPage />} />
            <Route path="plan" element={<WavePlannerPage />} />
            <Route path=":id" element={<WaveDetailPage />} />
          </Route>
          <Route path="wms/tasks" element={gateGroup('wms.tasks')}>
            <Route index element={<TasksListPage />} />
          </Route>
          <Route path="wms/asns" element={gateGroup('wms.asn')}>
            <Route index element={<AsnsListPage />} />
            <Route path="new" element={<AsnNewPage />} />
          </Route>
          <Route path="wms/dock" element={gateGroup('wms.dock')}>
            <Route index element={<DockListPage />} />
          </Route>
          <Route path="wms/goods-receipts" element={gateGroup('wms.grn')}>
            <Route index element={<GoodsReceiptsListPage />} />
            <Route path="new" element={<GoodsReceiptNewPage />} />
            <Route path=":id" element={<GoodsReceiptDetailPage />} />
          </Route>
          <Route path="wms/license-plates" element={gateGroup('wms.lp')}>
            <Route index element={<LicensePlatesListPage />} />
          </Route>
          <Route path="wms/outbound" element={g('wms.waves', <OutboundPlannerPage />)} />

          <Route path="service">
            <Route path="calls" element={g('service.calls', <ServiceCallsPage />)} />
            <Route path="calls/new" element={g('service.calls', <ServiceCallFormPage />)} />
            <Route path="calls/:id" element={g('service.calls', <ServiceCallDetailPage />)} />
            <Route path="jobs" element={g('service.jobs', <ServiceJobsPage />)} />
            <Route path="jobs/new" element={g('service.jobs', <ServiceJobDetailPage />)} />
            <Route path="jobs/:id" element={g('service.jobs', <ServiceJobDetailPage />)} />
            <Route path="dispatch" element={g('service.jobs', <DispatchBoardPage />)} />
            <Route path="assets" element={g('service.assets', <ServiceAssetsPage />)} />
            <Route path="assets/new" element={g('service.assets', <ServiceAssetFormPage />)} />
            <Route path="assets/:id" element={g('service.assets', <ServiceAssetDetailPage />)} />
            <Route path="rmas" element={g('service.rmas', <ServiceRmasPage />)} />
            <Route path="rmas/new" element={g('service.rmas', <ServiceRmaDetailPage />)} />
            <Route path="rmas/:id" element={g('service.rmas', <ServiceRmaDetailPage />)} />
            <Route path="contracts" element={g('service.contracts', <ServiceContractsPage />)} />
            <Route path="contracts/new" element={g('service.contracts', <ServiceContractDetailPage />)} />
            <Route path="contracts/:id" element={g('service.contracts', <ServiceContractDetailPage />)} />
          </Route>

          <Route path="customers" element={gateGroup('sales.customers')}>
            <Route index element={<CustomersPage />} />
            <Route path="new" element={<CustomerFormPage />} />
            <Route path=":id" element={<CustomerFormPage />} />
            <Route path=":id/edit" element={<CustomerFormPage />} />
          </Route>

          <Route path="hr/employees" element={gateGroup('hr.employees')}>
            <Route index element={<EmployeeListPage />} />
            <Route path=":id" element={<EmployeeDetailPage />} />
          </Route>
          <Route path="hr/positions" element={gateGroup('hr.positions')}>
            <Route index element={<PositionsPage />} />
          </Route>
          <Route path="hr/payroll/runs" element={gateGroup('hr.payroll')}>
            <Route index element={<PayRunListPage />} />
            <Route path=":id" element={<PayRunDetailPage />} />
          </Route>
          <Route path="hr/payroll/config" element={g('hr.payroll.config', <PayrollConfigPage />)} />
          <Route path="hr/payroll/forms" element={g('hr.payroll.forms', <YearEndFormsPage />)} />
          <Route path="hr/benefits/plans" element={gateGroup('hr.benefits')}>
            <Route index element={<BenefitPlansPage />} />
          </Route>
          <Route path="hr/benefits/enrollments" element={gateGroup('hr.benefits')}>
            <Route index element={<EnrollmentsPage />} />
            <Route path=":id" element={<EnrollmentDetail />} />
          </Route>
          <Route path="hr/benefits/compliance" element={g('hr.benefits.compliance', <CompliancePage />)} />

          {/* HR self-service portal (Wave C). Each route is gated by its
              portal.* node; record scope (own / report-subtree) is enforced
              in the backend handlers. Team is additionally gated portal.team. */}
          <Route path="portal/profile" element={g('portal.profile', <MyProfilePage />)} />
          <Route path="portal/pay" element={g('portal.pay', <MyPayPage />)} />
          <Route path="portal/benefits" element={g('portal.benefits', <MyBenefitsPage />)} />
          <Route path="portal/team" element={g('portal.team', <MyTeamPage />)} />

          <Route path="ar-invoices" element={gateGroup('receivables.invoices')}>
            <Route index element={<ArInvoicesPage />} />
            <Route path="new" element={<ArInvoiceNewPage />} />
            {/* The static segment outranks :id, so /ar-invoices/lines is the line log. */}
            <Route path="lines" element={<ArInvoiceLinesPage />} />
            <Route path=":id" element={<ArInvoiceDetailPage />} />
            {/* /edit kept for back-compat links; the detail page is itself
                editable while the invoice is a draft. */}
            <Route path=":id/edit" element={<ArInvoiceDetailPage />} />
          </Route>

          <Route path="credit-notes" element={gateGroup('receivables.credit_notes')}>
            <Route index element={<CreditNotesPage />} />
            {/* "+ New" creates an unnumbered stub and lands on the detail form
                — same document mechanics as invoices and orders. */}
            <Route path="new" element={<CreditNoteNewPage />} />
            <Route path=":id" element={<CreditNoteDetailPage />} />
          </Route>

          <Route path="receipts" element={gateGroup('receivables.receipts')}>
            <Route index element={<ReceiptsPage />} />
            <Route path="new" element={<ReceiptNewPage />} />
            <Route path=":id/edit" element={<ReceiptFormPage />} />
            <Route path=":id" element={<ReceiptDetailPage />} />
          </Route>

          <Route path="contra-settlements" element={gateGroup('receivables.contra_settlements')}>
            <Route index element={<ContraSettlementsPage />} />
            <Route path="new" element={<ContraSettlementDetailPage />} />
            <Route path=":id" element={<ContraSettlementDetailPage />} />
          </Route>

          <Route path="contracts" element={gateGroup('receivables.contracts')}>
            <Route index element={<ContractsPage />} />
            <Route path="new" element={<ContractFormPage />} />
            <Route path=":id/edit" element={<ContractFormPage />} />
            <Route path=":id" element={<ContractDetailPage />} />
          </Route>

          <Route path="vendors" element={gateGroup('payables.vendors')}>
            <Route index element={<VendorsPage />} />
            <Route path="new" element={<VendorFormPage />} />
            <Route path=":id" element={<VendorFormPage />} />
            <Route path=":id/edit" element={<VendorFormPage />} />
          </Route>

          <Route path="ap-invoices" element={gateGroup('payables.bills')}>
            <Route index element={<ApInvoicesPage />} />
            <Route path="new" element={<ApInvoiceFormPage />} />
            <Route path="scan" element={<ScanInvoicePage />} />
            <Route path="scan-inbox" element={<ScanInboxPage />} />
            <Route path="lines" element={<ApInvoiceLinesPage />} />
            <Route path=":id" element={<ApInvoiceDetailPage />} />
            <Route path=":id/edit" element={<ApInvoiceFormPage />} />
          </Route>

          <Route path="payments" element={gateGroup('payables.payments')}>
            <Route index element={<PaymentsPage />} />
            <Route path="new" element={<PaymentFormPage />} />
            <Route path=":id" element={<PaymentDetailPage />} />
            {/* Drafts are edited inline on the detail page — the legacy edit route redirects there. */}
            <Route path=":id/edit" element={<Navigate to=".." relative="path" replace />} />
          </Route>
          <Route path="payables/pay-bills" element={g('payables.payments', <PayBillsPage />)} />
          {/* Bank payment files (NACHA / CPA 005) built from posted payments. */}
          <Route path="payables/bank-files" element={gateGroup('payables.bank_files')}>
            <Route index element={<BankFilesPage />} />
            <Route path=":id" element={<BankFileDetailPage />} />
          </Route>

          <Route path="accounts" element={g('ledger.accounts', <AccountsPage />)} />
          <Route path="accounts/:id/ledger" element={g('ledger.accounts', <AccountLedgerPage />)} />
          <Route path="cashflow" element={g('ledger.balance_sheet', <CashFlowPage />)} />
          <Route path="collections" element={g('receivables.invoices', <CollectionsPage />)} />
          <Route path="financial-overview" element={g('ledger.profit_loss', <BIDashboardPage />)} />
          <Route path="vendor-hub" element={g('payables.vendors', <VendorsHubPage />)} />
          <Route path="expense-categories" element={g('payables.vendors', <ExpenseCategoriesPage />)} />
          <Route path="copilot" element={<CopilotPage />} />
          <Route path="bank-accounts" element={gateGroup('ledger.bank_accounts')}>
            <Route index element={<BankAccountsPage />} />
            <Route path="new" element={<BankAccountFormPage />} />
            <Route path=":id" element={<BankAccountFormPage />} />
            <Route path=":id/edit" element={<BankAccountFormPage />} />
            <Route path=":id/statements/import" element={<BankStatementImportPage />} />
          </Route>
          <Route path="banking" element={gateGroup('ledger.bank_accounts')}>
            <Route index element={<BankingPage />} />
            <Route path="reconciliations" element={<BankingPage />} />
          </Route>
          <Route path="bank-statements" element={gateGroup('ledger.bank_accounts')}>
            <Route path=":id" element={<BankStatementDetailPage />} />
          </Route>
          <Route path="bank-reconciliations" element={gateGroup('ledger.bank_accounts')}>
            <Route path=":id" element={<BankReconciliationPage />} />
          </Route>
          <Route path="journal" element={gateGroup('ledger.journal')}>
            <Route index element={<JournalPage />} />
            <Route path="new" element={<JournalFormPage />} />
            <Route path="recurring" element={<RecurringJournalsPage />} />
            <Route path="recurring/new" element={<RecurringJournalFormPage />} />
            <Route path="recurring/:rid" element={<RecurringJournalFormPage />} />
            <Route path=":id" element={<JournalDetailPage />} />
            <Route path=":id/edit" element={<JournalFormPage />} />
          </Route>
          <Route path="ledger/settings" element={g('ledger.settings', <LedgerSettingsPage />)} />
          <Route path="receivables/settings" element={g('receivables.settings', <ReceivablesSettingsPage />)} />
          <Route path="receivables/quickbooks" element={g('receivables.quickbooks', <QuickBooksTransfersPage />)} />
          <Route path="receivables/reports" element={g('receivables.reports', <ReportsPage />)} />
          <Route path="receivables/reports/new" element={g('receivables.reports', <ArReportBuilderPage />)} />
          <Route path="receivables/reports/b/:key" element={g('receivables.reports', <ArReportBuilderPage />)} />
          <Route path="receivables/reports/:id" element={g('receivables.reports', <ArReportBuilderPage />)} />

          <Route path="reports">
            <Route path="ar-aging" element={g('receivables.ar_aging', <ArAgingReport />)} />
            <Route path="ap-aging" element={g('payables.ap_aging', <ApAgingReport />)} />
            <Route path="trial-balance" element={g('ledger.trial_balance', <TrialBalanceReport />)} />
            <Route path="tax-summary" element={g('ledger.tax_codes', <TaxSummaryReport />)} />
            <Route path="control-reconciliation" element={g('ledger.trial_balance', <ControlReconciliationReport />)} />
            <Route path="inventory-valuation" element={g('ledger.trial_balance', <InventoryValuationReport />)} />
            <Route path="profit-loss" element={g('ledger.profit_loss', <ProfitLossReport />)} />
            <Route path="balance-sheet" element={g('ledger.balance_sheet', <BalanceSheetReport />)} />
            <Route path="cash-flow" element={g('ledger.balance_sheet', <CashFlowStatementReport />)} />
            <Route path="ledger-audit" element={g('ledger', <LedgerAuditReport />)} />
            <Route path="fx-exposure" element={g('ledger.balance_sheet', <FxExposureReport />)} />
            <Route path="statements" element={g('receivables.statements', <StatementsPage />)} />
          </Route>

          <Route path="admin">
            {/* Tenant Users page: add staff, assign roles, reset passwords,
                deactivate. Superadmins still have the cross-tenant matrix
                under Manage Companies. */}
            <Route path="users" element={g('admin.users', <UsersPage />)} />
            <Route path="company" element={g('admin.company', <CompanySettingsPage />)} />
            <Route path="fiscal-periods" element={g('ledger.settings', <FiscalPeriodsPage />)} />
            <Route path="posting-rules" element={g('ledger.settings', <PostingRulesPage />)} />
            <Route path="customization" element={g('admin.customization', <CustomizationHub />)} />
            <Route path="custom-fields" element={g('admin.customization', <CustomFieldsPage />)} />
            <Route path="menus" element={g('admin.customization', <MenuManagerPage />)} />
            <Route path="report-builder" element={g('admin.customization', <ReportBuilderPage />)} />
            <Route path="form-designer" element={g('admin.customization', <FormDesignerPage />)} />
            <Route path="custom-objects" element={g('admin.customization', <CustomObjectsPage />)} />
            <Route path="dashboards" element={g('admin.customization', <CustomDashboardsPage />)} />
            <Route path="automation" element={g('admin.customization', <AutomationPage />)} />
            <Route path="checks" element={g('admin.checks', <ChecksPage />)} />
            <Route path="workflows" element={g('admin.customization', <WorkflowsPage />)} />
            <Route path="procedures" element={g('admin.procedures', <ProceduresPage />)} />
            <Route path="procedures/:id" element={g('admin.procedures', <ProcedureEditorPage />)} />
            <Route path="governance" element={g('admin.customization', <GovernancePage />)} />
            <Route path="api-tokens" element={g('admin.customization', <ApiTokensPage />)} />
            <Route path="webhooks" element={g('admin.webhooks', <WebhooksPage />)} />
            <Route path="integrations" element={g('admin.integrations', <IntegrationsPage />)} />
            <Route path="new-company" element={g('admin.new_company', <NewCompanyPage />)} />
            <Route path="document-numbering" element={g('admin.document_numbering', <DocumentNumberingPage />)} />
            <Route path="document-types" element={g('admin.company', <DocumentTypesPage />)} />
            <Route path="document-templates" element={g('admin.document_templates', <DocumentTemplatesPage />)} />
            <Route path="roles" element={g('admin.roles', <RoleWorkspacePage />)} />
            <Route path="roles/:id" element={g('admin.roles', <RoleWorkspacePage />)} />
            <Route path="audit" element={g('admin.roles', <AuditEventsPage />)} />
            <Route path="branches" element={gateGroup('admin.branches')}>
              <Route index element={<BranchesPage />} />
              <Route path="new" element={<BranchFormPage />} />
              <Route path=":id/edit" element={<BranchFormPage />} />
            </Route>
            <Route path="departments" element={g('admin.departments', <DepartmentsPage />)} />
          </Route>

          {/* Custom objects (Phase 6) — generic list + form for any defined object. */}
          <Route path="custom/:key" element={<CustomObjectListPage />} />
          <Route path="custom/:key/new" element={<CustomObjectFormPage />} />
          <Route path="custom/:key/:id" element={<CustomObjectFormPage />} />

          <Route path="coming-soon/:what" element={<ComingSoonPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="appearance" element={<AppearancePage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="change-password" element={<ChangePasswordPage />} />
          <Route path="settings/email" element={<EmailConfigPage />} />
          <Route path="settings/email-templates" element={<EmailTemplatesPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
